import { FormControl, FormField, FormItem } from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useController, useFormContext } from 'react-hook-form';
import { Label } from './ui/label';

export const FormFieldRadioGroup = ({
  label,
  name,
  options,
  direction = 'vertical',
  required,
  defaultValue,
  onChange,
  disabled = false,
  optionsInNextLine = false,
}: {
  label?: string;
  name: string;
  required?: boolean;
  options: {
    value: string;
    label: string;
  }[];
  defaultValue?: string;
  direction: 'horizontal' | 'vertical';
  onChange?: (value: string | null) => void;
  disabled?: boolean;
  optionsInNextLine?: boolean;
}) => {
  const { control } = useFormContext();
  const { field } = useController({ name, control });

  return (
    <FormField
      control={control}
      name={name}
      render={({ fieldState }) => (
        <>
          <FormItem className={`flex ${optionsInNextLine ? 'flex-col' : 'flex-row'} gap-4`}>
            {label && (
              <Label className={`whitespace-nowrap ${optionsInNextLine ? 'pb-1' : 'self-end'}`}>
                {label} {required && <span className="text-error align-top">*</span>}
              </Label>
            )}
            <FormControl>
              <RadioGroup
                disabled={disabled}
                value={(field.value as string) ?? defaultValue ?? ''}
                onValueChange={(value) => {
                  field.onChange(value);
                  onChange?.(value);
                }}
                className={`flex ${
                  direction === 'horizontal' ? 'flex-row gap-4 md:gap-8 !mt-0' : 'flex-col space-y-1'
                }`}
              >
                {options.map((option, index) => (
                  <FormItem key={option.value} className="flex items-center space-x-3 space-y-0 !mt-0">
                    <RadioGroupItem
                      value={option.value}
                      id={`${name}-${option.value}`}
                      tabIndex={0}
                      onBlur={index === options.length - 1 ? field.onBlur : undefined}
                      onClick={() => {
                        if (field.value === option.value) {
                          field.onChange(null);
                          onChange?.(null);
                        } else {
                          field.onChange(option.value);
                          onChange?.(option.value);
                        }
                      }}
                    />
                    <Label htmlFor={`${name}-${option.value}`} className="font-medium cursor-pointer">
                      {option.label}
                    </Label>
                  </FormItem>
                ))}
              </RadioGroup>
            </FormControl>
          </FormItem>
          {fieldState.error && <p className="text-error text-xs mt-1">{fieldState.error.message}</p>}
        </>
      )}
    />
  );
};
